import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getBinFilterOptions } from 'common/charts/constants';
import { getAxisToBackendDirection, getDirectionDisplayName } from '../../../utils/directionLabels';
import {
  CouplingContainer,
  ModernCouplingSection,
  CollapsibleContent,
  CouplingDescriptionWrapper
} from './styled';
import CouplingToggle from './CouplingToggle';
import CouplingHeader from './CouplingHeader';
import CouplingStatusTooltip from './CouplingStatusTooltip';
import SensorSelectionSection from './SensorSelectionSection';
import ConfigurationFields from './ConfigurationFields';
import ThresholdsDisplay from './ThresholdsDisplay';
import CouplingConfirmAlert from '../CouplingConfirmAlert';

const AlarmThresholdCouplingControl = ({
  coupledToAlarmThresholds,
  couplingConfig,
  alarmThresholds,
  fetchingAlarms,
  conditions,
  onChange,
  temperatureOnly,
  vibrationDirectionPreference = 1,
  dashboardCouplingEnabled = false,
  otherCouplingActive = false,
  embedded = false,
  user
}) => {
  const { t } = useTranslation();

  // Derive condition types from existing conditions
  const existingConditionTypes = React.useMemo(() => {
    const types = new Set();
    if (conditions && Array.isArray(conditions)) {
      conditions.forEach((condition) => {
        if (condition.metric) {
          types.add(condition.metric);
        }
      });
    }
    return Array.from(types);
  }, [conditions]);

  // Extract missing alarms from alarmThresholds
  const missingAlarms = (alarmThresholds && alarmThresholds.missing) || null;

  const [localConfig, setLocalConfig] = useState(couplingConfig || {
    speed_bin: 'bin_0',
    vibration_features: [], // Array to support multiple features
    threshold_level: 'critical',
    coupled_sensor_types: []
  });

  const [isUpdatingConditions, setIsUpdatingConditions] = useState(false);
  const [featureDropdownOpen, setFeatureDropdownOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [unavailableFeatures, setUnavailableFeatures] = useState([]);

  // Sync localConfig when couplingConfig prop changes
  useEffect(() => {
    if (couplingConfig) {
      // Migrate old vibration_feature (string) to vibration_features (array) if needed
      const migratedConfig = { ...couplingConfig };
      if (couplingConfig.vibration_feature && !couplingConfig.vibration_features) {
        // Normalize legacy feature value
        const legacyFeature = couplingConfig.vibration_feature;
        const normalizedFeature = (legacyFeature === 'velocity' || legacyFeature === 'velocity_rms')
          ? 'velocity'
          : 'acceleration';
        migratedConfig.vibration_features = [normalizedFeature];
        delete migratedConfig.vibration_feature;
      } else if (!couplingConfig.vibration_features) {
        migratedConfig.vibration_features = [];
      }
      setLocalConfig(migratedConfig);
    } else {
      setLocalConfig({
        speed_bin: 'bin_0',
        vibration_features: [],
        threshold_level: 'critical',
        coupled_sensor_types: []
      });
    }
  }, [couplingConfig]);

  // Auto-remove unavailable sensor types and features when thresholds become unavailable
  useEffect(() => {
    if (!coupledToAlarmThresholds || !alarmThresholds) {
      return;
    }

    const updatedConfig = { ...localConfig };
    let configChanged = false;

    // Remove temperature if thresholds are not available
    if (updatedConfig.coupled_sensor_types && updatedConfig.coupled_sensor_types.includes(getSensorTypeValue('Temperature'))) {
      if (!checkTemperatureThresholdsAvailable()) {
        updatedConfig.coupled_sensor_types = updatedConfig.coupled_sensor_types.filter(t => t !== getSensorTypeValue('Temperature'));
        configChanged = true;
      }
    }

    // Remove vibration if no features are available
    if (updatedConfig.coupled_sensor_types && updatedConfig.coupled_sensor_types.includes(getSensorTypeValue('Vibration'))) {
      const speedBin = updatedConfig.speed_bin || 'bin_0';
      const hasVelocity = checkVibrationFeatureThresholdsAvailable('velocity', speedBin);
      const hasAcceleration = checkVibrationFeatureThresholdsAvailable('acceleration', speedBin);

      if (!hasVelocity && !hasAcceleration) {
        // No features available - remove vibration
        updatedConfig.coupled_sensor_types = updatedConfig.coupled_sensor_types.filter(t => t !== getSensorTypeValue('Vibration'));
        configChanged = true;
      } else {
        // Remove unavailable features from vibration_features
        const currentFeatures = updatedConfig.vibration_features || [];
        const availableFeatures = currentFeatures.filter((feature) => {
          if (feature === 'velocity') {
            return hasVelocity;
          }
          if (feature === 'acceleration') {
            return hasAcceleration;
          }
          return false;
        });

        // Always ensure vibration_features only contains available features
        // Always update to ensure unavailable features are removed
        if (availableFeatures.length > 0) {
          // Check if arrays are actually different before updating
          const currentSorted = [...currentFeatures].sort();
          const availableSorted = [...availableFeatures].sort();
          if (JSON.stringify(currentSorted) !== JSON.stringify(availableSorted)) {
            updatedConfig.vibration_features = availableFeatures;
            configChanged = true;
          } else {
            // Even if same, ensure the array is set correctly
            updatedConfig.vibration_features = availableFeatures;
          }
        } else if (hasVelocity) {
          // No features available from current selection - pre-select velocity first (original default)
          updatedConfig.vibration_features = ['velocity'];
          configChanged = true;
        } else if (hasAcceleration) {
          // Fall back to acceleration if velocity not available
          updatedConfig.vibration_features = ['acceleration'];
          configChanged = true;
        } else {
          // No features available at all - remove vibration entirely
          updatedConfig.coupled_sensor_types = updatedConfig.coupled_sensor_types.filter(t => t !== getSensorTypeValue('Vibration'));
          // Also clear vibration_features
          updatedConfig.vibration_features = [];
          configChanged = true;
        }
      }
    }

    if (configChanged) {
      setLocalConfig(updatedConfig);
      // Update conditions with new config
      const updatedConditions = mapAlarmThresholdsToConditions(conditions, updatedConfig, alarmThresholds);
      onChange({
        coupling_config: cleanCouplingConfig(updatedConfig),
        conditions: updatedConditions
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarmThresholds, localConfig.speed_bin, localConfig.threshold_level, coupledToAlarmThresholds, cleanCouplingConfig]);

  // Available sensor types - always show both unless temperatureOnly
  const hasTemperature = true; // Always show temperature option
  const hasVibration = !temperatureOnly; // Only available if not temperatureOnly

  // Check if there are conditions of each type to couple
  const hasTemperatureCondition = existingConditionTypes.includes('Temperature');
  const hasVibrationCondition = existingConditionTypes.includes('Vibration');

  // Map sensor type names to enum values
  const getSensorTypeValue = useCallback((name) => {
    if (name === 'Temperature') return 0;
    if (name === 'Vibration') return 2;
    return null;
  }, []);

  // Check if temperature thresholds are available
  const checkTemperatureThresholdsAvailable = useCallback(() => {
    if (!alarmThresholds || !alarmThresholds.temperature) {
      return false;
    }
    const temp = alarmThresholds.temperature;
    // Check if both warning and critical thresholds exist
    return (temp.warning !== undefined && temp.warning !== null) &&
           (temp.critical !== undefined && temp.critical !== null);
  }, [alarmThresholds]);

  // Check if all required directions exist for a vibration feature
  const checkVibrationFeatureThresholdsAvailable = useCallback((feature, speedBin) => {
    if (!alarmThresholds || !alarmThresholds.vibration) {
      return false;
    }

    const vibrationData = alarmThresholds.vibration;
    const axisToBackendDirection = getAxisToBackendDirection(vibrationDirectionPreference);
    const requiredDirections = Object.values(axisToBackendDirection);
    const thresholdLevel = localConfig.threshold_level || 'critical';

    // Check if all required directions exist and have the required threshold
    const allDirectionsAvailable = requiredDirections.every((direction) => {
      if (!vibrationData[direction]) {
        return false;
      }

      const directionData = vibrationData[direction];
      const binData = directionData[speedBin || localConfig.speed_bin || 'bin_0'];

      if (!binData) {
        return false;
      }

      // Check if the required threshold key exists for this feature
      let thresholdKey;
      if (feature === 'velocity') {
        thresholdKey = thresholdLevel;
      } else if (feature === 'acceleration') {
        thresholdKey = `acceleration_${thresholdLevel}`;
      } else {
        return false;
      }

      return binData[thresholdKey] !== undefined && binData[thresholdKey] !== null;
    });

    return allDirectionsAvailable;
  }, [alarmThresholds, vibrationDirectionPreference, localConfig.threshold_level, localConfig.speed_bin]);

  // Get display label for backend direction based on account preference
  const getDirectionDisplayLabel = useCallback((backendDirection) => {
    const axisToBackendDirection = getAxisToBackendDirection(vibrationDirectionPreference);
    const direction = Object.values(axisToBackendDirection).includes(backendDirection)
      ? backendDirection
      : 'axial';
    return getDirectionDisplayName(t, direction);
  }, [t, vibrationDirectionPreference]);

  // Get reason why a vibration feature is unavailable
  const getFeatureUnavailableReason = useCallback((feature, speedBin) => {
    if (!alarmThresholds || !alarmThresholds.vibration) {
      return t('sensors.eventDetection.coupling.vibrationThresholdsNotConfigured');
    }

    const vibrationData = alarmThresholds.vibration;
    const axisToBackendDirection = getAxisToBackendDirection(vibrationDirectionPreference);
    const requiredDirections = Object.values(axisToBackendDirection);
    const thresholdLevel = localConfig.threshold_level || 'critical';
    const missingDirections = [];

    requiredDirections.forEach((direction) => {
      if (!vibrationData[direction]) {
        const directionLabel = getDirectionDisplayLabel(direction);
        missingDirections.push(directionLabel);
        return;
      }

      const directionData = vibrationData[direction];
      const binData = directionData[speedBin || localConfig.speed_bin || 'bin_0'];

      if (!binData) {
        const directionLabel = getDirectionDisplayLabel(direction);
        missingDirections.push(t('sensors.eventDetection.coupling.missingDirectionSpeedBin', {
          direction: directionLabel
        }));
        return;
      }

      let thresholdKey;
      if (feature === 'velocity') {
        thresholdKey = thresholdLevel;
      } else if (feature === 'acceleration') {
        thresholdKey = `acceleration_${thresholdLevel}`;
      } else {
        return;
      }

      if (binData[thresholdKey] === undefined || binData[thresholdKey] === null) {
        const directionLabel = getDirectionDisplayLabel(direction);
        missingDirections.push(t('sensors.eventDetection.coupling.missingDirectionThreshold', {
          direction: directionLabel,
          thresholdLevel: t(`sensors.eventDetection.coupling.${thresholdLevel}`)
        }));
      }
    });

    if (missingDirections.length === 0) {
      return null;
    }

    const featureName = feature === 'velocity'
      ? t('sensors.eventDetection.coupling.velocity')
      : t('sensors.eventDetection.coupling.acceleration');
    return t('sensors.eventDetection.coupling.featureThresholdsUnavailable', {
      feature: featureName,
      missing: missingDirections.join(', ')
    });
  }, [alarmThresholds, vibrationDirectionPreference, localConfig.threshold_level, localConfig.speed_bin, getDirectionDisplayLabel, t]);

  // Track which features are unavailable for the current speed bin (for tooltip + UI)
  useEffect(() => {
    if (!coupledToAlarmThresholds || !alarmThresholds) {
      setUnavailableFeatures([]);
      return;
    }

    const speedBin = localConfig.speed_bin || 'bin_0';
    const unavailable = [];

    // Velocity
    if (!checkVibrationFeatureThresholdsAvailable('velocity', speedBin)) {
      unavailable.push({
        feature: t('sensors.eventDetection.coupling.velocity'),
        reason: getFeatureUnavailableReason('velocity', speedBin) || t('sensors.eventDetection.coupling.velocityUnavailableForSpeedBin')
      });
    }

    // Acceleration
    if (!checkVibrationFeatureThresholdsAvailable('acceleration', speedBin)) {
      unavailable.push({
        feature: t('sensors.eventDetection.coupling.acceleration'),
        reason: getFeatureUnavailableReason('acceleration', speedBin) || t('sensors.eventDetection.coupling.accelerationUnavailableForSpeedBin')
      });
    }

    setUnavailableFeatures(unavailable);
  }, [localConfig.speed_bin, alarmThresholds, coupledToAlarmThresholds, checkVibrationFeatureThresholdsAvailable, getFeatureUnavailableReason, t]);

  // Map condition metricType to coupling feature name
  const metricTypeToFeature = useCallback((metricType) => {
    if (metricType === 'Velocity RMS' || metricType === 'Velocity Peak' || metricType === 'Velocity P2P') {
      return 'velocity';
    }
    if (metricType === 'Accel RMS' || metricType === 'Accel Peak' || metricType === 'Accel P2P') {
      return 'acceleration';
    }
    return null;
  }, []);

  // Map alarm thresholds to conditions - updates conditions with threshold values
  const mapAlarmThresholdsToConditions = useCallback((conditionsToMap, config, thresholds) => {
    if (!conditionsToMap || !config || !thresholds || !config.coupled_sensor_types) {
      return conditionsToMap;
    }
    const coupledSensorTypes = config.coupled_sensor_types || [];
    const thresholdLevel = config.threshold_level || 'critical';

    return conditionsToMap.map((condition) => {
      const sensorTypeValue = getSensorTypeValue(condition.metric);

      // Only update conditions that are coupled
      if (!coupledSensorTypes.includes(sensorTypeValue)) {
        return condition;
      }

      const updatedCondition = Object.assign({}, condition);

      // Update operator to ">" for coupled conditions
      updatedCondition.operator = '>';

      // Handle Temperature
      if (condition.metric === 'Temperature' && thresholds.temperature) {
        const thresholdValue = thresholds.temperature[thresholdLevel];
        if (thresholdValue !== undefined && thresholdValue !== null) {
          // Round to 2 decimal places to match stored values
          updatedCondition.thresholds = { value: parseFloat(thresholdValue.toFixed(2)) };
        }
      }

      // Handle Vibration
      if (condition.metric === 'Vibration' && thresholds.vibration) {
        // Only update conditions whose metricType matches one of the selected features
        const conditionFeature = metricTypeToFeature(condition.metricType);
        const selectedFeatures = config.vibration_features || [];

        // Skip if this condition's feature is not in the selected features
        if (!conditionFeature || !selectedFeatures.includes(conditionFeature)) {
          return condition;
        }

        const speedBin = config.speed_bin || 'bin_0';

        // Map axis to backend direction based on account preference
        // TAR (pref=1): 1→tangential, 2→axial, 3→radial
        // HAV (pref=2): 1→horizontal, 2→axial, 3→vertical
        const axisToBackendDirection = getAxisToBackendDirection(vibrationDirectionPreference);
        // Normalize axis: for Vibration, empty/undefined axis means all axes [1,2,3]
        const axis = (condition.axis && condition.axis.length > 0) ? condition.axis : [1, 2, 3];

        // Ensure axis is set on the updated condition
        updatedCondition.axis = axis;

        const thresholdValues = {};
        axis.forEach((axisVal) => {
          if (axisVal !== 0) {
            const backendDirection = axisToBackendDirection[axisVal];
            if (backendDirection && thresholds.vibration[backendDirection]) {
              const directionData = thresholds.vibration[backendDirection][speedBin];
              if (directionData) {
                let value = null;

                // Backend stores thresholds as flat keys:
                // velocity: 'warning', 'critical'
                // acceleration: 'acceleration_warning', 'acceleration_critical'
                if (conditionFeature === 'velocity') {
                  value = directionData[thresholdLevel]; // 'warning' or 'critical'
                } else if (conditionFeature === 'acceleration') {
                  value = directionData[`acceleration_${thresholdLevel}`]; // 'acceleration_warning' or 'acceleration_critical'
                }

                if (value !== undefined && value !== null) {
                  let axisKey = 'x';
                  if (axisVal === 2) axisKey = 'y';
                  else if (axisVal === 3) axisKey = 'z';
                  // Round to 2 decimal places to match stored values
                  thresholdValues[axisKey] = parseFloat(value.toFixed(2));
                }
              }
            }
          }
        });

        // Only update if we have values for at least one axis
        if (Object.keys(thresholdValues).length > 0) {
          // Preserve existing structure, fill in missing axes with 0
          updatedCondition.thresholds = {
            x: thresholdValues.x !== undefined ? thresholdValues.x : (condition.thresholds && condition.thresholds.x) || 0,
            y: thresholdValues.y !== undefined ? thresholdValues.y : (condition.thresholds && condition.thresholds.y) || 0,
            z: thresholdValues.z !== undefined ? thresholdValues.z : (condition.thresholds && condition.thresholds.z) || 0
          };
        }
      }

      return updatedCondition;
    });
  }, [getSensorTypeValue, metricTypeToFeature, vibrationDirectionPreference]);

  // Re-apply thresholds when alarmThresholds are fetched/updated while coupling is already enabled
  useEffect(() => {
    // Only update if coupling was already enabled and we now have thresholds
    // (This handles the case where thresholds are fetched after initial toggle)
    if (coupledToAlarmThresholds && alarmThresholds && localConfig &&
        localConfig.coupled_sensor_types && localConfig.coupled_sensor_types.length > 0 &&
        conditions && conditions.length > 0) {
      setIsUpdatingConditions(true);

      const updatedConditions = mapAlarmThresholdsToConditions(conditions, localConfig, alarmThresholds);

      // Check if conditions actually changed
      let conditionsChanged = false;
      for (let i = 0; i < updatedConditions.length; i++) {
        const updated = updatedConditions[i];
        const original = conditions[i];
        if (JSON.stringify(updated.thresholds) !== JSON.stringify(original.thresholds) ||
            updated.operator !== original.operator) {
          conditionsChanged = true;
          break;
        }
      }

      if (conditionsChanged) {
        onChange({
          coupling_config: cleanCouplingConfig(localConfig),
          conditions: updatedConditions
        });
      }

      setTimeout(() => {
        setIsUpdatingConditions(false);
      }, 300);
    }
    // Only re-run when alarmThresholds change (after fetch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(alarmThresholds)]);

  // Determine missing alarm thresholds
  const temperatureMissing = useMemo(() =>
    hasTemperature && (missingAlarms && missingAlarms.temperature === true),
  [hasTemperature, missingAlarms]
  );

  // Backend directions match account preference (TAR or HAV)
  const axisToBackendDirection = useMemo(() =>
    getAxisToBackendDirection(vibrationDirectionPreference),
  [vibrationDirectionPreference]
  );

  const backendDirections = useMemo(() =>
    Object.values(axisToBackendDirection),
  [axisToBackendDirection]
  );

  const vibrationMissing = useMemo(() =>
    hasVibration && (missingAlarms && missingAlarms.vibration && backendDirections.some(dir => missingAlarms.vibration[dir] === true)),
  [hasVibration, missingAlarms, backendDirections]
  );

  const hasMissingAlarms = useMemo(() =>
    temperatureMissing || vibrationMissing,
  [temperatureMissing, vibrationMissing]
  );

  // Get available speed bins and format with descriptive labels
  const speedBinOptions = useMemo(() => {
    const availableBins = (alarmThresholds && alarmThresholds.available_bins) || ['bin_0'];

    // Extract numeric values from bin strings (e.g., "bin_0" -> 0, "bin_1" -> 1)
    const binNumbers = availableBins.map((bin) => {
      const match = bin.match(/bin_(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    });

    // Find maximum bin number to determine bin count
    const maxBinNumber = Math.max(...binNumbers, 0);

    // Get descriptive labels using getBinFilterOptions (include_zero=true to get "Default" for bin_0)
    const binFilterOptions = getBinFilterOptions(maxBinNumber, true, t);

    const axisToBackendDir = getAxisToBackendDirection(vibrationDirectionPreference);
    const requiredDirections = Object.values(axisToBackendDir);

    // Check if bin is available for ALL required directions
    const isBinAvailable = (bin) => {
      if (!alarmThresholds || !alarmThresholds.vibration) return false;
      return requiredDirections.every((direction) => {
        const dirData = alarmThresholds.vibration[direction];
        return dirData && dirData[bin] !== undefined;
      });
    };

    // Map to dropdown options, converting numeric values back to bin string format
    return binFilterOptions
      .filter(option => binNumbers.includes(option.value)) // Only include bins that are available
      .map((option) => {
        const binKey = `bin_${option.value}`;
        const available = isBinAvailable(binKey);
        const missingDirs = !available ? requiredDirections.filter((dir) => {
          const dirData = alarmThresholds && alarmThresholds.vibration && alarmThresholds.vibration[dir];
          return !dirData || !dirData[binKey];
        }).map(dir => getDirectionDisplayLabel(dir)) : [];

        return {
          key: binKey,
          text: option.text,
          value: binKey,
          disabled: !available,
          // Use hover-only tooltip via the native `title` attribute; avoid always-visible `description`
          title: !available ? t('sensors.eventDetection.coupling.notAvailableFor', { directions: missingDirs.join(', ') }) : undefined
        };
      });
  }, [alarmThresholds, vibrationDirectionPreference, getDirectionDisplayLabel, t]);

  // Helper function to clean coupling config before sending to backend
  // Removes vibration-specific fields when vibration is not coupled
  const cleanCouplingConfig = useCallback((config) => {
    if (!config) return config;

    const cleanedConfig = { ...config };
    const vibrationIsCoupled = cleanedConfig.coupled_sensor_types && cleanedConfig.coupled_sensor_types.includes(getSensorTypeValue('Vibration'));

    // Don't send vibration_features if vibration isn't coupled
    if (!vibrationIsCoupled) {
      delete cleanedConfig.vibration_features;
      delete cleanedConfig.speed_bin; // Also optional for temperature-only
      delete cleanedConfig.vibration_threshold_type;
    } else if (cleanedConfig.vibration_features && cleanedConfig.vibration_features.length === 0) {
      // If vibration is coupled but features array is empty, remove it (shouldn't happen but safety check)
      delete cleanedConfig.vibration_features;
    }

    return cleanedConfig;
  }, [getSensorTypeValue]);

  const [couplingConfirmMessage, setCouplingConfirmMessage] = useState(null);

  const applyAlarmCouplingToggle = useCallback((newEnabled) => {
    if (newEnabled) {
      // Alarm thresholds should already be fetched when edit mode is enabled
      // Check if we can enable coupling (no missing alarms)
      if (alarmThresholds) {
        // Validate temperature thresholds if temperature conditions exist
        const hasTempCondition = conditions && conditions.some(c => c.metric === 'Temperature');
        if (hasTempCondition && !checkTemperatureThresholdsAvailable()) {
          // eslint-disable-next-line no-console
          console.warn('[Coupling] Cannot enable - temperature alarm thresholds not available');
          return;
        }

        // Check vibration conditions and identify which features can be coupled
        const vibrationConditions = conditions && conditions.filter(c => c.metric === 'Vibration');
        const unavailableFeatures = [];

        if (vibrationConditions && vibrationConditions.length > 0) {
          const speedBin = localConfig.speed_bin || 'bin_0';
          const conditionFeatures = new Set();

          // Collect all features from existing vibration conditions
          vibrationConditions.forEach((condition) => {
            const feature = metricTypeToFeature(condition.metricType);
            if (feature) {
              conditionFeatures.add(feature);
            }
          });

          // Identify which features don't have thresholds available
          Array.from(conditionFeatures).forEach((feature) => {
            if (!checkVibrationFeatureThresholdsAvailable(feature, speedBin)) {
              const featureLabel = feature === 'velocity'
                ? t('sensors.eventDetection.coupling.velocity')
                : t('sensors.eventDetection.coupling.acceleration');
              const reason = getFeatureUnavailableReason(feature, speedBin);
              unavailableFeatures.push({ feature: featureLabel, reason });
            }
          });

          // If some features are unavailable, store them to display info message
          if (unavailableFeatures.length > 0) {
            // eslint-disable-next-line no-console
            console.info('[Coupling] Some features unavailable, will not be coupled:', unavailableFeatures);
            setUnavailableFeatures(unavailableFeatures);
          } else {
            setUnavailableFeatures([]);
          }
        }

        // Auto-populate coupled_sensor_types and vibration_features based on existing conditions
        const newConfig = Object.assign({}, localConfig);
        if (!newConfig.coupled_sensor_types || newConfig.coupled_sensor_types.length === 0) {
          const autoCoupledTypes = [];
          const autoVibrationFeatures = [];
          const speedBin = newConfig.speed_bin || 'bin_0';

          if (conditions && conditions.length > 0) {
            conditions.forEach((condition) => {
              if (condition.metric) {
                const sensorTypeValue = getSensorTypeValue(condition.metric);

                // Validate before adding
                if (condition.metric === 'Temperature') {
                  if (checkTemperatureThresholdsAvailable() && !autoCoupledTypes.includes(sensorTypeValue)) {
                    autoCoupledTypes.push(sensorTypeValue);
                  }
                } else if (condition.metric === 'Vibration') {
                  const feature = metricTypeToFeature(condition.metricType);
                  if (feature && checkVibrationFeatureThresholdsAvailable(feature, speedBin)) {
                    // Add vibration to coupled types if not already there
                    if (!autoCoupledTypes.includes(sensorTypeValue)) {
                      autoCoupledTypes.push(sensorTypeValue);
                    }
                    // Add feature to vibration_features if not already there
                    if (!autoVibrationFeatures.includes(feature)) {
                      autoVibrationFeatures.push(feature);
                    }
                  }
                }
              }
            });
          }

          newConfig.coupled_sensor_types = autoCoupledTypes;
          // Only set vibration_features if vibration is coupled and we have features
          if (autoCoupledTypes.includes(getSensorTypeValue('Vibration'))) {
            if (autoVibrationFeatures.length > 0) {
              newConfig.vibration_features = autoVibrationFeatures;
            } else {
              // No features from existing conditions, pre-select based on availability
              // Prefer velocity first (original default), fall back to acceleration
              const hasVelocity = checkVibrationFeatureThresholdsAvailable('velocity', speedBin);
              const hasAcceleration = checkVibrationFeatureThresholdsAvailable('acceleration', speedBin);

              if (hasVelocity) {
                newConfig.vibration_features = ['velocity'];
              } else if (hasAcceleration) {
                newConfig.vibration_features = ['acceleration'];
              }
            }
          }
        }

        // Enable coupling and update conditions with thresholds
        const updatedConditions = mapAlarmThresholdsToConditions(conditions, newConfig, alarmThresholds);
        // eslint-disable-next-line no-console
        console.log('[Coupling] Enabled with config:', newConfig, 'Updated conditions:', updatedConditions);

        // Auto-expand the section when enabling coupling
        setIsExpanded(true);

        onChange({
          coupling_config: cleanCouplingConfig(newConfig),
          dashboard_coupling_config: null,
          conditions: updatedConditions
        });
      } else {
        // No thresholds available yet (still loading)
        // Auto-populate coupled_sensor_types based on existing conditions if empty
        const newConfig = Object.assign({}, localConfig);
        if (!newConfig.coupled_sensor_types || newConfig.coupled_sensor_types.length === 0) {
          const autoCoupledTypes = [];
          if (conditions && conditions.length > 0) {
            conditions.forEach((condition) => {
              if (condition.metric) {
                const sensorTypeValue = getSensorTypeValue(condition.metric);
                if (sensorTypeValue !== null && !autoCoupledTypes.includes(sensorTypeValue)) {
                  autoCoupledTypes.push(sensorTypeValue);
                }
              }
            });
          }
          newConfig.coupled_sensor_types = autoCoupledTypes;
        }
        // Just enable coupling, conditions will update when thresholds arrive
        // Auto-expand the section when enabling coupling
        setIsExpanded(true);

        onChange({
          coupling_config: cleanCouplingConfig(newConfig),
          dashboard_coupling_config: null
        });
      }
    } else {
      // Disabling coupling
      setUnavailableFeatures([]);
      onChange({
        coupling_config: null
      });
    }
  }, [alarmThresholds, conditions, localConfig, checkTemperatureThresholdsAvailable, checkVibrationFeatureThresholdsAvailable, getFeatureUnavailableReason, getSensorTypeValue, metricTypeToFeature, mapAlarmThresholdsToConditions, onChange, cleanCouplingConfig, t]);

  const handleToggle = useCallback(() => {
    const newEnabled = !coupledToAlarmThresholds;
    if (newEnabled && dashboardCouplingEnabled) {
      setCouplingConfirmMessage(
        t('sensors.eventDetection.dashboardCoupling.confirmSwitchFromDashboard')
      );
      return;
    }
    applyAlarmCouplingToggle(newEnabled);
  }, [coupledToAlarmThresholds, dashboardCouplingEnabled, applyAlarmCouplingToggle, t]);

  const handleCouplingConfirmProceed = useCallback(() => {
    setCouplingConfirmMessage(null);
    applyAlarmCouplingToggle(true);
  }, [applyAlarmCouplingToggle]);

  const handleConfigUpdate = useCallback((key, value) => {
    const newConfig = { ...localConfig, [key]: value };

    // If updating speed_bin or threshold_level, also remove unavailable features
    if ((key === 'speed_bin' || key === 'threshold_level') && alarmThresholds && newConfig.coupled_sensor_types && newConfig.coupled_sensor_types.includes(getSensorTypeValue('Vibration'))) {
      const speedBin = key === 'speed_bin' ? value : (newConfig.speed_bin || 'bin_0');
      const currentFeatures = newConfig.vibration_features || [];

      // Filter out unavailable features
      const availableFeatures = currentFeatures.filter((feature) => {
        if (feature === 'velocity') {
          return checkVibrationFeatureThresholdsAvailable('velocity', speedBin);
        }
        if (feature === 'acceleration') {
          return checkVibrationFeatureThresholdsAvailable('acceleration', speedBin);
        }
        return false;
      });

      // Update vibration_features if any were removed
      if (availableFeatures.length !== currentFeatures.length) {
        newConfig.vibration_features = availableFeatures;
        // If no features left, try to pre-select an available one
        if (availableFeatures.length === 0) {
          const hasVelocity = checkVibrationFeatureThresholdsAvailable('velocity', speedBin);
          const hasAcceleration = checkVibrationFeatureThresholdsAvailable('acceleration', speedBin);

          // Prefer velocity first (original default), fall back to acceleration
          if (hasVelocity) {
            newConfig.vibration_features = ['velocity'];
          } else if (hasAcceleration) {
            newConfig.vibration_features = ['acceleration'];
          } else {
            // No features available - remove vibration from coupled_sensor_types
            newConfig.coupled_sensor_types = newConfig.coupled_sensor_types.filter(t => t !== getSensorTypeValue('Vibration'));
          }
        }
      }
    }

    setLocalConfig(newConfig);
    if (coupledToAlarmThresholds && alarmThresholds) {
      // Update conditions with new config
      const updatedConditions = mapAlarmThresholdsToConditions(conditions, newConfig, alarmThresholds);
      onChange({
        coupling_config: cleanCouplingConfig(newConfig),
        conditions: updatedConditions
      });
    }
  }, [localConfig, alarmThresholds, getSensorTypeValue, checkVibrationFeatureThresholdsAvailable, coupledToAlarmThresholds, conditions, mapAlarmThresholdsToConditions, onChange, cleanCouplingConfig]);

  const handleConditionTypeToggle = useCallback((sensorType) => {
    // Validate threshold availability before allowing toggle
    if (sensorType === getSensorTypeValue('Temperature')) {
      // Check if temperature thresholds are available
      if (!checkTemperatureThresholdsAvailable()) {
        // eslint-disable-next-line no-console
        console.warn('[Coupling] Cannot couple temperature - alarm thresholds not available');
        return;
      }
    } else if (sensorType === getSensorTypeValue('Vibration')) {
      // Check if at least one vibration feature has all directions available
      const speedBin = localConfig.speed_bin || 'bin_0';
      const hasVelocity = checkVibrationFeatureThresholdsAvailable('velocity', speedBin);
      const hasAcceleration = checkVibrationFeatureThresholdsAvailable('acceleration', speedBin);

      // If trying to enable vibration, check if at least one feature is available
      const currentTypes = localConfig.coupled_sensor_types || [];
      if (!currentTypes.includes(sensorType)) {
        // Enabling vibration - need at least one feature available
        if (!hasVelocity && !hasAcceleration) {
          // eslint-disable-next-line no-console
          console.warn('[Coupling] Cannot couple vibration - no features have all required directions');
          return;
        }

        // Pre-select features if none are selected
        const currentFeatures = localConfig.vibration_features || [];
        if (currentFeatures.length === 0) {
          const newConfig = { ...localConfig };
          // Prefer velocity first (original default), fall back to acceleration
          if (hasVelocity) {
            newConfig.vibration_features = ['velocity'];
          } else if (hasAcceleration) {
            newConfig.vibration_features = ['acceleration'];
          }
          newConfig.coupled_sensor_types = [...currentTypes, sensorType];

          setLocalConfig(newConfig);
          if (coupledToAlarmThresholds && alarmThresholds) {
            const updatedConditions = mapAlarmThresholdsToConditions(conditions, newConfig, alarmThresholds);
            onChange({
              coupling_config: cleanCouplingConfig(newConfig),
              conditions: updatedConditions
            });
          }
          return;
        }
      }
    }

    const currentTypes = localConfig.coupled_sensor_types || [];
    const newTypes = currentTypes.includes(sensorType)
      ? currentTypes.filter(t => t !== sensorType)
      : [...currentTypes, sensorType];

    handleConfigUpdate('coupled_sensor_types', newTypes);
  }, [getSensorTypeValue, checkTemperatureThresholdsAvailable, checkVibrationFeatureThresholdsAvailable, localConfig, handleConfigUpdate, coupledToAlarmThresholds, alarmThresholds, mapAlarmThresholdsToConditions, conditions, onChange]);

  // Get display values for alarm thresholds
  const getAlarmThresholdDisplay = useCallback(() => {
    if (!alarmThresholds || !coupledToAlarmThresholds || !localConfig.coupled_sensor_types || localConfig.coupled_sensor_types.length === 0) {
      return null;
    }

    const displays = [];
    const coupledTypes = localConfig.coupled_sensor_types || [];
    const thresholdLevel = localConfig.threshold_level || 'critical';

    // Get user's unit system (1 = metric, 2 = imperial)
    const unitSystem = (user && user.unit_system) || 1;
    const isImperial = unitSystem === 2;

    // Temperature
    if (coupledTypes.includes(getSensorTypeValue('Temperature')) && alarmThresholds.temperature) {
      const tempValue = alarmThresholds.temperature[thresholdLevel];
      if (tempValue !== undefined && tempValue !== null) {
        const tempUnit = isImperial ? '°F' : '°C';
        displays.push({
          type: 'Temperature',
          value: `${tempValue.toFixed(2)}${tempUnit}`,
          level: thresholdLevel
        });
      }
    }

    // Vibration
    if (coupledTypes.includes(getSensorTypeValue('Vibration')) && alarmThresholds.vibration) {
      const speedBin = localConfig.speed_bin || 'bin_0';
      const features = localConfig.vibration_features || [];

      // Backend directions match account preference
      const axisToBackendDir = getAxisToBackendDirection(vibrationDirectionPreference);
      const directions = Object.values(axisToBackendDir);

      // Display for each selected feature
      features.forEach((feature) => {
        directions.forEach((direction) => {
          const directionData = alarmThresholds.vibration[direction];
          if (directionData && directionData[speedBin]) {
            const binData = directionData[speedBin];
            let value = null;
            let unit = '';
            let featureLabel = '';

            // Backend stores thresholds as flat keys:
            // velocity: 'warning', 'critical'
            // acceleration: 'acceleration_warning', 'acceleration_critical'
            if (feature === 'velocity') {
              value = binData[thresholdLevel]; // 'warning' or 'critical'
              unit = isImperial ? 'in/s' : 'mm/s';
              featureLabel = t('sensors.eventDetection.coupling.velocity');
            } else if (feature === 'acceleration') {
              value = binData[`acceleration_${thresholdLevel}`]; // 'acceleration_warning' or 'acceleration_critical'
              unit = 'g'; // g is universal for both unit systems
              featureLabel = t('sensors.eventDetection.coupling.acceleration');
            }

            if (value !== undefined && value !== null) {
              const directionLabel = getDirectionDisplayLabel(direction);
              displays.push({
                type: `${t('sensors.eventDetection.vibration')} ${featureLabel} (${directionLabel})`,
                value: `${value.toFixed(2)} ${unit}`,
                level: thresholdLevel
              });
            }
          }
        });
      });
    }

    return displays;
  }, [alarmThresholds, coupledToAlarmThresholds, localConfig.coupled_sensor_types, localConfig.threshold_level, localConfig.speed_bin, localConfig.vibration_features, getSensorTypeValue, vibrationDirectionPreference, getDirectionDisplayLabel, user, t]);

  const alarmDisplays = useMemo(() => getAlarmThresholdDisplay(), [getAlarmThresholdDisplay]);

  const handleFeatureToggle = useCallback((newFeatures) => {
    handleConfigUpdate('vibration_features', newFeatures);
  }, [handleConfigUpdate]);

  // Calculate if toggle should be disabled and why
  const couplingDisabledState = useMemo(() => {
    if (otherCouplingActive) {
      return {
        disabled: true,
        reason: t('sensors.eventDetection.couplingSource.disableOtherFirstAlarm')
      };
    }

    // Can't enable if no conditions exist
    if (!hasTemperatureCondition && !hasVibrationCondition) {
      return {
        disabled: true,
        reason: t('sensors.eventDetection.coupling.noConditionsDisabledReason')
      };
    }

    // Can't enable if alarms haven't loaded yet or don't exist
    if (!alarmThresholds) {
      return {
        disabled: true,
        reason: t('sensors.eventDetection.coupling.allAlarmThresholdsMissing')
      };
    }

    // Check if any condition can be coupled
    const canCoupleTemp = hasTemperatureCondition && checkTemperatureThresholdsAvailable();
    const canCoupleVib = hasVibrationCondition && (
      checkVibrationFeatureThresholdsAvailable('velocity', localConfig.speed_bin) ||
      checkVibrationFeatureThresholdsAvailable('acceleration', localConfig.speed_bin)
    );

    const canCouple = canCoupleTemp || canCoupleVib;

    // If we have conditions but can't couple any of them
    if (!canCouple) {
      return {
        disabled: true,
        reason: t('sensors.eventDetection.coupling.requiredThresholdsUnavailable')
      };
    }

    return {
      disabled: false,
      reason: null
    };
  }, [hasTemperatureCondition, hasVibrationCondition, alarmThresholds,
    checkTemperatureThresholdsAvailable, checkVibrationFeatureThresholdsAvailable,
    localConfig.speed_bin, otherCouplingActive, t]);

  // Build status tooltip if there are issues or partial coupling
  const statusTooltip = (alarmThresholds && (hasMissingAlarms || unavailableFeatures.length > 0)) ? (
    <CouplingStatusTooltip
      temperatureMissing={temperatureMissing}
      vibrationMissing={vibrationMissing}
      missingAlarms={missingAlarms}
      axisToBackendDirection={axisToBackendDirection}
      getDirectionDisplayLabel={getDirectionDisplayLabel}
      unavailableFeatures={unavailableFeatures}
    />
  ) : null;

  return (
    <CouplingContainer embedded={embedded}>
      {couplingConfirmMessage && (
        <CouplingConfirmAlert
          title={t('sensors.eventDetection.dashboardCoupling.confirmSwitchTitle')}
          message={couplingConfirmMessage}
          onProceed={handleCouplingConfirmProceed}
          onCancel={() => setCouplingConfirmMessage(null)}
        />
      )}
      {!coupledToAlarmThresholds ? (
        <CouplingToggle
          enabled={false}
          fetchingAlarms={fetchingAlarms}
          onToggle={handleToggle}
          statusTooltip={statusTooltip}
          disabled={couplingDisabledState.disabled}
          disabledReason={couplingDisabledState.reason}
        />
      ) : (
        <ModernCouplingSection embedded={embedded}>
          <CouplingHeader
            enabled={coupledToAlarmThresholds}
            isExpanded={isExpanded}
            fetchingAlarms={fetchingAlarms}
            isUpdatingConditions={isUpdatingConditions}
            onToggle={handleToggle}
            onToggleExpand={() => setIsExpanded(!isExpanded)}
            statusTooltip={statusTooltip}
          />

          <CollapsibleContent isExpanded={isExpanded}>
            <ConfigurationFields
              localConfig={localConfig}
              speedBinOptions={speedBinOptions}
              showSpeedBin={hasVibration && hasVibrationCondition}
              onConfigUpdate={handleConfigUpdate}
            />

            <SensorSelectionSection
              hasTemperature={hasTemperature}
              hasVibration={hasVibration}
              hasTemperatureCondition={hasTemperatureCondition}
              hasVibrationCondition={hasVibrationCondition}
              localConfig={localConfig}
              alarmThresholds={alarmThresholds}
              speedBin={localConfig.speed_bin}
              featureDropdownOpen={featureDropdownOpen}
              unavailableFeatures={unavailableFeatures}
              getSensorTypeValue={getSensorTypeValue}
              checkTemperatureThresholdsAvailable={checkTemperatureThresholdsAvailable}
              checkVibrationFeatureThresholdsAvailable={checkVibrationFeatureThresholdsAvailable}
              getFeatureUnavailableReason={getFeatureUnavailableReason}
              onConditionTypeToggle={handleConditionTypeToggle}
              onFeatureDropdownToggle={() => setFeatureDropdownOpen(!featureDropdownOpen)}
              onFeatureToggle={handleFeatureToggle}
            />

            <ThresholdsDisplay alarmDisplays={alarmDisplays} user={user} />
          </CollapsibleContent>
        </ModernCouplingSection>
      )}

      {!coupledToAlarmThresholds && !alarmThresholds && (
        <CouplingDescriptionWrapper>
          {t('sensors.eventDetection.coupling.description')}
        </CouplingDescriptionWrapper>
      )}
    </CouplingContainer>
  );
};

AlarmThresholdCouplingControl.propTypes = {
  coupledToAlarmThresholds: PropTypes.bool.isRequired,
  couplingConfig: PropTypes.object,
  alarmThresholds: PropTypes.object,
  fetchingAlarms: PropTypes.bool,
  conditions: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  temperatureOnly: PropTypes.bool,
  vibrationDirectionPreference: PropTypes.number,
  dashboardCouplingEnabled: PropTypes.bool,
  otherCouplingActive: PropTypes.bool,
  embedded: PropTypes.bool,
  user: PropTypes.object
};

AlarmThresholdCouplingControl.defaultProps = {
  couplingConfig: null,
  alarmThresholds: null,
  fetchingAlarms: false,
  temperatureOnly: false,
  vibrationDirectionPreference: 1,
  dashboardCouplingEnabled: false,
  otherCouplingActive: false,
  embedded: false,
  user: null
};

export default AlarmThresholdCouplingControl;
